// All display times are in the browser's local time zone via Intl.

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const timeSecFmt = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
const dayTimeFmt = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
const dateFmt = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
const fullFmt = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "medium" });
const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto", style: "short" });

export function parseTs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "2:14 PM" today, "Sep 21, 2:14 PM" otherwise. */
export function fmtWhen(iso: string | null | undefined, withSeconds = false): string {
  const t = parseTs(iso);
  if (t === null) return "never";
  const d = new Date(t);
  if (isSameDay(d, new Date())) return (withSeconds ? timeSecFmt : timeFmt).format(d);
  return dayTimeFmt.format(d);
}

export function fmtFull(iso: string | null | undefined): string {
  const t = parseTs(iso);
  return t === null ? "" : fullFmt.format(new Date(t));
}

export function fmtDate(iso: string | null | undefined): string {
  const t = parseTs(iso);
  return t === null ? "" : dateFmt.format(new Date(t));
}

/** "12 sec. ago", "3 min. ago", "yesterday". */
export function fmtAgo(iso: string | null | undefined, now: number): string {
  const t = parseTs(iso);
  if (t === null) return "never";
  return fmtAgoMs(now - t);
}

export function fmtAgoMs(diffMs: number): string {
  const s = Math.round(diffMs / 1000);
  if (Math.abs(s) < 5) return "just now";
  if (Math.abs(s) < 60) return rtf.format(-s, "second");
  const m = Math.round(s / 60);
  if (Math.abs(m) < 60) return rtf.format(-m, "minute");
  const h = Math.round(m / 60);
  if (Math.abs(h) < 24) return rtf.format(-h, "hour");
  const d = Math.round(h / 24);
  return rtf.format(-d, "day");
}

/** "0:42" style countdown. */
export function fmtCountdown(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Compact age for HUD callouts: "just now", "17s ago", "2m ago", "1h ago", "3d ago". */
export function fmtAgoShort(iso: string | null | undefined, now: number): string {
  const t = parseTs(iso);
  if (t === null) return "never";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
